<template>
    <div
        class="sbs-file-uploader"
        :class="uploaderClasses">
        <div class="sbs-file-uploader__wrapper">
            <input
                v-bind="uploaderInputData"
                ref="uploaderInput"
                class="sbs-file-uploader__input visually-hidden"
                @focus="focusHandler"
                @blur="blurHandler"
                @click="clickHandler"
                @change="changeHandler">

            <label
                class="sbs-file-uploader__label trs"
                :class="uploaderLabelClasses"
                :for="uploaderId"
                @dragover.prevent=""
                @dragenter.prevent="dragenterHandler"
                @dragleave.prevent="dragleaveHandler"
                @drop.prevent="dropHandler"
                @mousedown="mousedownHandler"
                @mouseup="mouseupHandler">
                <!-- @slot Слот по-умолчанию -->
                <slot>
                    <div class="sbs-file-uploader__label-wrapper">
                        <SbsLink
                            class="sbs-file-uploader__link"
                            text="Прикрепить файл"
                            colorTheme="currentColor">
                            <template #prepend>
                                <div>
                                    <SbsIcon
                                        class="sbs-file-uploader__link-icon"
                                        iconName="paperclip" />
                                </div>
                            </template>
                        </SbsLink>

                        <span
                            v-if="hasError"
                            class="sbs-file-uploader__note sbs-file-uploader__note--errors">
                            {{ joinedErrorMessages }}
                        </span>

                        <span
                            v-else
                            class="sbs-file-uploader__note sbs-file-uploader__note--caption">
                            Размер не&nbsp;больше {{ maxFileSize }}&nbsp;Мб
                        </span>
                    </div>
                </slot>
            </label>
        </div>
    </div>
</template>

<script>
import themesMixin from '../../mixins/themes.js';
import { getRandomId, deleteKeysFromObject } from '../../utils/helpers.js';

/**
 * Загрузчик файлов
 */
export default {
    name: 'SbsFileUploader',
    mixins: [themesMixin],
    data() {
        return {
            isMousedownActive: false,
        };
    },
    computed: {
        uploaderClasses() {
            return [
                {
                    'drag-and-drop': this.dragAndDrop,
                    'disabled': this.disabled,
                    'invalid': this.hasError,
                    'mousedown-active': this.isMousedownActive,
                },
            ];
        },
        hasError() {
            return this.errorMessages.length > 0;
        },
        uploaderId() {
            return this.id || `sbs-file-uploader-${getRandomId()}`;
        },
        joinedErrorMessages() {
            return this.errorMessages.join('. ');
        },
        uploaderInputData() {
            const attrs = deleteKeysFromObject(this.$attrs, ['onChange']);

            return {
                id: this.uploaderId,
                class: [{ 'visually-hidden': !this.dragAndDrop }],
                accept: this.acceptFileExtensions,
                disabled: this.disabled,
                multiple: this.multiple,
                title: '',
                type: 'file',
                ...attrs,
            };
        },
        uploaderLabelClasses() {
            return [...this.themeClasses, this.labelClass];
        },
    },
    methods: {
        getUpdatedFiles(files) {
            return [...files].map(file => {
                file.id = getRandomId();

                return file;
            });
        },
        focusHandler(e) {
            if (this.disabled) return;

            this.$emit('focus', e);
        },
        blurHandler(e) {
            if (this.disabled) return;

            this.$emit('blur', e);
            this.isMousedownActive = false;
        },
        clickHandler(e) {
            if (this.disabled) return;

            this.$emit('click', e);

            if (this.$refs.uploaderInput?.blur) {
                this.$refs.uploaderInput.blur();
            }
        },
        changeHandler(e) {
            if (this.disabled) return;

            const files = this.getUpdatedFiles(e.target.files);

            this.$emit('change', files);

            if (this.$refs.uploaderInput?.blur) {
                this.$refs.uploaderInput.blur();
            }
        },
        dragenterHandler(e) {
            if (this.disabled) return;

            this.$emit('dragenter', e);
        },
        dragleaveHandler(e) {
            if (this.disabled) return;

            this.$emit('dragleave', e);
        },
        dropHandler(e) {
            if (this.disabled) return;

            const files = this.getUpdatedFiles(e.dataTransfer.files);

            this.$emit('drop', e);

            if (files.length) {
                this.$emit('change', files);
            }
        },
        mousedownHandler() {
            if (this.disabled) return;

            this.isMousedownActive = true;
        },
        mouseupHandler() {
            this.isMousedownActive = false;
        },
    },
    inheritAttrs: false,
    props: {
        /**
         * Допустимые типы файлов
         */
        acceptFileExtensions: {
            type: Array,
            default: () => [],
        },
        /**
         * Отключает любые действия с компонентом
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Включает возможность добавлять файлы перетаскиванием
         */
        dragAndDrop: {
            type: Boolean,
            default: false,
        },
        /**
         * Сообщения об ошибках
         */
        errorMessages: {
            type: Array,
            default: () => [],
        },
        /**
         * Список загруженных файлов
         */
        files: {
            type: Array,
            default: () => [],
        },
        labelClass: {
            type: String,
            default: '',
        },
        /**
         * Допустимый размер загружаемого файла в мегабайтах
         */
        maxFileSize: {
            type: Number,
            default: 70,
        },
        /**
         * Флаг загрузки нескольких файлов
         */
        multiple: {
            type: Boolean,
            default: false,
        },
        /**
         * Уникальный идентификатор
         */
        id: {
            type: String,
            default: '',
        },
    },
    watch: {
        files() {
            if (this.$refs.uploaderInput) {
                this.$refs.uploaderInput.value = '';
            }
        },
    },
};
</script>

<style lang="scss">
.sbs-file-uploader {
    position: relative;
    display: flex;
    flex-direction: column;
    max-width: 100%;
    height: 100%;

    &.disabled {
        cursor: not-allowed;
        opacity: 0.5;
        user-select: none;

        & > * {
            pointer-events: none;
        }
    }

    &__wrapper {
        display: flex;
        flex-direction: column;
        height: 100%;
    }

    &__label {
        display: flex;
        align-items: center;
        height: 100%;
        cursor: pointer;

        .sbs-file-uploader__input:not(:disabled):active + & {
            opacity: 0.6;
        }

        &::after {
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            z-index: 10;
            content: "";
        }
    }

    &__label-wrapper {
        display: flex;
        flex-direction: column;
        width: 100%;
    }

    &__link-icon {
        margin-right: 5px;
    }

    &__note {
        display: block;
        margin-top: 5px;

        &--errors {
            color: $crimson;

            @include typo-subsecondary-book;
        }

        &--caption {
            color: $gray-deep;

            @include typo-additional-book;
        }
    }
}
</style>
